import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { createPortal } from "react-dom";
import "../attorneyDashboardPages/ClientDatePicker.css";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  return year > 0 && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};
const parseDate = (value) => validDate(value) || new Date();
const formatTypedDate = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("-");
};
const maskedDate = (value) => {
  const formatted = formatTypedDate(value || "");
  return formatted + "MM-DD-YYYY".slice(formatted.length);
};
const displayTypedDate = (value = "") => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value || "";
  const [year, month, day] = value.split("-");
  return `${month}-${day}-${year}`;
};

export default function SetupBirthDatePicker({ value, onChange, onBlur, id = "setup-dob", name = "dob", label = "Choose date of birth", className = "", menuClassName = "", portal = false, showToday = false, dateFormat = "mm/dd/yyyy", allowTyping = false, drop = "down", align = "start" }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("days");
  const [display, setDisplay] = useState(() => parseDate(value));
  const trigger = useRef(null);
  const input = useRef(null);
  useEffect(() => {
    input.current?.setCustomValidity(value && !validDate(value) ? "Enter a valid date in MM-DD-YYYY format." : "");
  }, [value, allowTyping]);
  const year = display.getFullYear();
  const month = display.getMonth();
  const startYear = Math.floor(year / 12) * 12;
  const firstDay = new Date(year, month, 1).getDay();
  const dayCount = new Date(year, month + 1, 0).getDate();
  const formatted = value ? (dateFormat === "yyyy-mm-dd" ? value : `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`) : dateFormat;

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    trigger.current?.focus();
  };

  const move = (direction) => {
    setDisplay(new Date(year + (view === "years" ? direction * 12 : view === "months" ? direction : 0), month + (view === "days" ? direction : 0), 1));
  };

  const calendarMenu = (
      <Dropdown.Menu className={`setup-birth-date-menu ${menuClassName}`} role="dialog" data-date-picker={id} aria-label={label} popperConfig={{ strategy: portal ? "fixed" : "absolute", modifiers: [{ name: "preventOverflow", options: { padding: 12 } }] }}>
        <div className="setup-calendar-header">
          <button type="button" onClick={() => move(-1)} aria-label={view === "years" ? "Previous years" : view === "months" ? "Previous year" : "Previous month"}>‹</button>
          <div>
            <button type="button" onClick={() => setView("months")}>{months[month]}</button>
            <button type="button" onClick={() => setView("years")}>{view === "years" ? `${startYear}–${startYear + 11}` : year}</button>
          </div>
          <button type="button" onClick={() => move(1)} aria-label={view === "years" ? "Next years" : view === "months" ? "Next year" : "Next month"}>›</button>
        </div>
        {view === "years" ? (
          <div className="setup-calendar-choices" aria-label="Choose year">
            {Array.from({ length: 12 }, (_, index) => startYear + index).map((item) => (
              <button type="button" key={item} onClick={() => { setDisplay(new Date(item, month, 1)); setView("months"); }}>{item}</button>
            ))}
          </div>
        ) : view === "months" ? (
          <div className="setup-calendar-choices" aria-label="Choose month">
            {months.map((item, index) => (
              <button type="button" key={item} onClick={() => { setDisplay(new Date(year, index, 1)); setView("days"); }}>{item.slice(0, 3)}</button>
            ))}
          </div>
        ) : (
          <>
            <div className="setup-calendar-week" aria-hidden="true">{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => <span key={day}>{day}</span>)}</div>
            <div className="setup-calendar-days" role="group" aria-label={`${months[month]} ${year}`}>
              {Array.from({ length: firstDay }, (_, index) => <span key={`empty-${index}`} />)}
              {Array.from({ length: dayCount }, (_, index) => index + 1).map((day) => {
                const isoDate = `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                return <button key={day} type="button" aria-label={`${months[month]} ${day}, ${year}`} aria-pressed={value === isoDate} onClick={() => choose(isoDate)}>{day}</button>;
              })}
            </div>
          </>
        )}
        <div className="setup-calendar-footer"><button type="button" onClick={() => choose("")}>Clear</button>{showToday && <button type="button" onClick={() => {
          const today = new Date();
          choose(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`);
        }}>Today</button>}</div>
      </Dropdown.Menu>
  );

  return (
    <Dropdown
      drop={drop}
      align={align}
      className={`setup-birth-date ${className}`}
      show={open}
      autoClose="outside"
      onToggle={(nextOpen) => {
        if (nextOpen) {
          setDisplay(parseDate(value));
          setView("days");
        }
        setOpen(nextOpen);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget) && !event.relatedTarget?.closest?.(`[data-date-picker="${id}"]`)) onBlur?.({ target: { name, id } });
      }}
    >
      {allowTyping && <input
        ref={input}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        className="form-control setup-birth-date-input"
        aria-description="Enter date of birth as month, day, year: MM-DD-YYYY."
        pattern="[0-9]{2}-[0-9]{2}-[0-9]{4}|MM-DD-YYYY"
        value={maskedDate(displayTypedDate(value))}
        onFocus={(event) => {
          if (!value) event.target.setSelectionRange(0, 0);
        }}
        onClick={(event) => {
          const field = event.target;
          const end = (value || "").length;
          if (field.selectionStart === field.selectionEnd && field.selectionStart > end) field.setSelectionRange(end, end);
        }}
        onChange={(event) => {
          const field = event.target;
          const digitsBeforeCursor = field.value.slice(0, field.selectionStart).replace(/\D/g, "").length;
          const nextValue = formatTypedDate(field.value);
          const cursor = Math.min(nextValue.length, digitsBeforeCursor + (digitsBeforeCursor > 2 ? 1 : 0) + (digitsBeforeCursor > 4 ? 1 : 0));
          const [month, day, year] = nextValue.split("-");
          const invalidMonth = month.length === 2 && (Number(month) < 1 || Number(month) > 12);
          const invalidDay = day?.length === 2 && (Number(day) < 1 || Number(day) > 31);
          if (invalidMonth || invalidDay) {
            const previousValue = displayTypedDate(value);
            const previousCursor = Math.min(previousValue.length, Math.max(0, cursor - 1));
            field.value = maskedDate(previousValue);
            field.setSelectionRange(previousCursor, previousCursor);
            return;
          }
          field.value = maskedDate(nextValue);
          field.setSelectionRange(cursor, cursor);
          const formValue = year?.length === 4 ? `${year}-${month}-${day}` : nextValue;
          onChange(formValue);
          const nextDate = validDate(formValue);
          if (nextDate) {
            setDisplay(nextDate);
            setView("days");
          }
        }}
      />}
      <Dropdown.Toggle ref={trigger} id={allowTyping ? `${id}-calendar` : id} variant="" className={allowTyping ? "setup-birth-date-calendar" : "form-control setup-birth-date-trigger"} aria-label={allowTyping ? label : undefined} aria-haspopup="dialog">
        {!allowTyping && <span>{formatted}</span>}
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="3" /><path d="M7 3v4m10-4v4M3 11h18" />
        </svg>
      </Dropdown.Toggle>
      {open && (portal && typeof document !== 'undefined' ? createPortal(calendarMenu, document.body) : calendarMenu)}
    </Dropdown>
  );
}
